import { useState } from 'react';
import { View, Text, TextInput, StyleSheet, Platform, ScrollView, Pressable } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles, addColorOpacity } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { AmortizationScheduleEntry } from '../../shared/types/mortgage';

interface AfterXYearsViewProps {
  schedule: AmortizationScheduleEntry[];
  loanTermYears: number;
  loanAmount: number;
}

export function AfterXYearsView({ schedule, loanTermYears, loanAmount }: AfterXYearsViewProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);
  
  const [yearsInput, setYearsInput] = useState('5');
  const years = Math.min(Math.max(1, parseInt(yearsInput) || 1), loanTermYears);
  const paymentsToShow = years * 12;
  const relevantPayments = schedule.slice(0, paymentsToShow);
  
  const totalPrincipalPaid = relevantPayments.reduce((sum, p) => sum + p.principal_payment, 0);
  const totalInterestPaid = relevantPayments.reduce((sum, p) => sum + p.interest_payment, 0);
  const totalPaid = totalPrincipalPaid + totalInterestPaid;
  const remainingBalance = relevantPayments.length > 0 
    ? relevantPayments[relevantPayments.length - 1].remaining_balance 
    : loanAmount;
  const equityBuilt = loanAmount - remainingBalance;
  const equityPercentage = (equityBuilt / loanAmount) * 100;

  // Quick year buttons
  const quickYears = [1, 3, 5, 10, 15, 20, 25, 30].filter(y => y <= loanTermYears);

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={true}>
      <View style={styles.header}>
        <Text style={styles.title}>After X Years Analysis</Text>
        <Text style={styles.subtitle}>
          See how much principal and interest you'll have paid after a specific number of years
        </Text>
      </View>

      <View style={styles.inputSection}>
        <Text style={styles.inputLabel}>Number of Years</Text>
        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            value={yearsInput}
            onChangeText={(text) => {
              const num = parseInt(text);
              if (text === '' || (!isNaN(num) && num >= 1 && num <= loanTermYears)) {
                setYearsInput(text);
              }
            }}
            keyboardType="numeric"
            placeholder={`1-${loanTermYears}`}
            placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.6)}
          />
          <Text style={styles.inputHint}>years (max {loanTermYears})</Text>
        </View>
        
        {/* Quick Year Buttons */}
        <View style={styles.quickYearsContainer}>
          <Text style={styles.quickYearsLabel}>Quick select:</Text>
          <View style={styles.quickYearsButtons}>
            {quickYears.map((y) => (
              <Pressable
                key={y}
                onPress={() => {
                  setYearsInput(y.toString());
                }}
                style={[
                  styles.quickYearButton,
                  years === y && styles.quickYearButtonActive,
                ]}
              >
                <Text style={[
                  styles.quickYearButtonText,
                  years === y && styles.quickYearButtonTextActive,
                ]}>
                  {y}yr
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      </View>

      {/* Summary Cards */}
      <View style={styles.summaryGrid}>
        <View style={[styles.summaryCard, styles.summaryCardHighlight]}>
          <Text style={styles.summaryCardLabel}>Principal Paid</Text>
          <Text style={styles.summaryCardValue}>
            ${totalPrincipalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <Text style={styles.summaryCardSubtext}>
            {((totalPrincipalPaid / loanAmount) * 100).toFixed(1)}% of loan
          </Text>
        </View>

        <View style={styles.summaryCard}>
          <Text style={styles.summaryCardLabel}>Interest Paid</Text>
          <Text style={[styles.summaryCardValue, styles.interestValue]}>
            ${totalInterestPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <Text style={styles.summaryCardSubtext}>
            {((totalInterestPaid / totalPaid) * 100).toFixed(1)}% of payments
          </Text>
        </View>

        <View style={styles.summaryCard}>
          <Text style={styles.summaryCardLabel}>Total Paid</Text>
          <Text style={styles.summaryCardValue}>
            ${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <Text style={styles.summaryCardSubtext}>
            Over {years} {years === 1 ? 'year' : 'years'}
          </Text>
        </View>

        <View style={styles.summaryCard}>
          <Text style={styles.summaryCardLabel}>Remaining Balance</Text>
          <Text style={styles.summaryCardValue}>
            ${remainingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <Text style={styles.summaryCardSubtext}>
            {((remainingBalance / loanAmount) * 100).toFixed(1)}% of loan
          </Text>
        </View>

        <View style={[styles.summaryCard, styles.equityCard]}>
          <Text style={styles.summaryCardLabel}>Equity Built</Text>
          <Text style={[styles.summaryCardValue, styles.equityValue]}>
            ${equityBuilt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <Text style={styles.summaryCardSubtext}>
            {equityPercentage.toFixed(1)}% of loan amount
          </Text>
        </View>

        <View style={styles.summaryCard}>
          <Text style={styles.summaryCardLabel}>Principal vs Interest</Text>
          <View style={styles.ratioBar}>
            <View 
              style={[
                styles.ratioBarSegment, 
                styles.ratioBarPrincipal,
                { width: `${(totalPrincipalPaid / totalPaid) * 100}%` }
              ]} 
            />
            <View 
              style={[
                styles.ratioBarSegment, 
                styles.ratioBarInterest,
                { width: `${(totalInterestPaid / totalPaid) * 100}%` }
              ]} 
            />
          </View>
          <Text style={styles.summaryCardSubtext}>
            {((totalPrincipalPaid / totalPaid) * 100).toFixed(1)}% principal, {((totalInterestPaid / totalPaid) * 100).toFixed(1)}% interest
          </Text>
        </View>
      </View>

      {/* Year Breakdown */}
      <View style={styles.yearBreakdown}>
        <Text style={styles.yearBreakdownTitle}>Year-by-Year Breakdown (First {years} {years === 1 ? 'Year' : 'Years'})</Text>
        {Array.from({ length: years }, (_, i) => {
          const year = i + 1;
          const startIdx = i * 12;
          const endIdx = Math.min((i + 1) * 12, relevantPayments.length);
          const yearPayments = relevantPayments.slice(startIdx, endIdx);
          const yearPrincipal = yearPayments.reduce((sum, p) => sum + p.principal_payment, 0);
          const yearInterest = yearPayments.reduce((sum, p) => sum + p.interest_payment, 0);
          const yearTotal = yearPrincipal + yearInterest;
          
          return (
            <View key={year} style={styles.yearRow}>
              <Text style={styles.yearNumber}>Year {year}</Text>
              <View style={styles.yearDetails}>
                <View style={styles.yearDetailItem}>
                  <Text style={styles.yearDetailLabel}>Principal</Text>
                  <Text style={[styles.yearDetailValue, styles.principalValue]}>
                    ${yearPrincipal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </View>
                <View style={styles.yearDetailItem}>
                  <Text style={styles.yearDetailLabel}>Interest</Text>
                  <Text style={[styles.yearDetailValue, styles.interestValue]}>
                    ${yearInterest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </View>
                <View style={styles.yearDetailItem}>
                  <Text style={styles.yearDetailLabel}>Total</Text>
                  <Text style={styles.yearDetailValue}>
                    ${yearTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </View>
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    header: {
      marginBottom: utils.spacing.lg,
    },
    title: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.heading.fontFamily,
    },
    subtitle: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    inputSection: {
      marginBottom: utils.spacing.lg,
      padding: utils.spacing.lg,
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    inputLabel: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      marginBottom: utils.spacing.sm,
      fontFamily: utils.typography.body.fontFamily,
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.md,
      marginBottom: utils.spacing.md,
    },
    input: {
      flex: 1,
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.md,
      backgroundColor: tokens.color.background,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontFamily: utils.typography.body.fontFamily,
      minWidth: 100,
    },
    inputHint: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    quickYearsContainer: {
      marginTop: utils.spacing.md,
    },
    quickYearsLabel: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.8),
      marginBottom: utils.spacing.sm,
      fontFamily: utils.typography.body.fontFamily,
    },
    quickYearsButtons: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.sm,
    },
    quickYearButton: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.sm,
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: tokens.color.border,
      backgroundColor: tokens.color.background,
    },
    quickYearButtonActive: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    quickYearButtonText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.medium,
    },
    quickYearButtonTextActive: {
      color: tokens.color.onPrimary,
      fontWeight: utils.typography.weights.semibold,
    },
    summaryGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
      marginBottom: utils.spacing.lg,
    },
    summaryCard: {
      flex: 1,
      minWidth: 150,
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      padding: utils.spacing.lg,
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)',
      }),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.03,
      shadowRadius: 4,
      elevation: 1,
    },
    summaryCardHighlight: {
      backgroundColor: addColorOpacity(tokens.color.primaryContainer, 0.2),
      borderColor: tokens.color.primary,
    },
    equityCard: {
      backgroundColor: addColorOpacity(tokens.color.success || tokens.color.primary, 0.1),
    },
    summaryCardLabel: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.8),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    summaryCardValue: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    interestValue: {
      color: addColorOpacity(tokens.color.onSurface, 0.7),
    },
    equityValue: {
      color: tokens.color.success || tokens.color.primary,
    },
    summaryCardSubtext: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    ratioBar: {
      height: 8,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.border, 0.3),
      flexDirection: 'row',
      overflow: 'hidden',
      marginBottom: utils.spacing.xs,
    },
    ratioBarSegment: {
      height: '100%',
    },
    ratioBarPrincipal: {
      backgroundColor: tokens.color.success || tokens.color.primary,
    },
    ratioBarInterest: {
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.4),
    },
    yearBreakdown: {
      marginTop: utils.spacing.lg,
    },
    yearBreakdownTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.md,
      fontFamily: utils.typography.heading.fontFamily,
    },
    yearRow: {
      marginBottom: utils.spacing.md,
      padding: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    yearNumber: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.primary,
      marginBottom: utils.spacing.sm,
      fontFamily: utils.typography.body.fontFamily,
    },
    yearDetails: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    yearDetailItem: {
      flex: 1,
      minWidth: 100,
    },
    yearDetailLabel: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    yearDetailValue: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    principalValue: {
      color: tokens.color.success || tokens.color.primary,
    },
  });
}
